import CheckIcon from "@mui/icons-material/Check"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Row from "../../../common/components/Row"
import {
  isConcierge,
  isExecutiveCaller,
  usesAccountOwnerHeader,
} from "../../users/accountRole"
import { promptDailyReportSubmission } from "../../users/dailyReportPrompt"
import { openSaveSessionNotesModal } from "../../users/openSaveSessionNotesModal"
import {
  selectActiveAccountId,
  selectProfile,
  switchActiveAccount,
} from "../../users/reducer"
import MemberAccountBadge from "./MemberAccountBadge"

export default function AccountSwitcher({
  ownerName,
  badgeVariant = "member",
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const axios = useAuthorizedAxiosClient()
  const profile = useSelector(selectProfile)
  const activeAccountId = useSelector(selectActiveAccountId)

  const [anchorEl, setAnchorEl] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [switchingId, setSwitchingId] = useState(null)

  const canSwitchAccounts = usesAccountOwnerHeader(profile)
  // Concierge/EC: show menu whenever any assignment exists (dropdown lists all).
  // Team members usually have one account; only show chevron when multiple.
  const showSwitcher =
    canSwitchAccounts &&
    (isConcierge(profile) || isExecutiveCaller(profile)
      ? accounts.length > 0
      : accounts.length > 1)

  useEffect(() => {
    if (!canSwitchAccounts) {
      setAccounts([])
      return
    }
    let cancelled = false
    setLoadingList(true)
    axios
      .get("/clients/api/accessible-accounts/")
      .then(({ data }) => {
        if (!cancelled) {
          setAccounts(Array.isArray(data) ? data : [])
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccounts([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingList(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [axios, canSwitchAccounts, profile?.id, profile?.role])

  function handleOpen(event) {
    if (!showSwitcher) {
      return
    }
    setAnchorEl(event.currentTarget)
  }

  function handleClose() {
    setAnchorEl(null)
  }

  async function handleSelectAccount(account) {
    const targetId = Number(account?.id)
    const currentId = Number(activeAccountId)
    if (!targetId || targetId === currentId) {
      handleClose()
      return
    }
    setSwitchingId(targetId)
    try {
      const payload = await dispatch(switchActiveAccount(targetId)).unwrap()
      const prompt = payload?.daily_report_prompt
      if (prompt?.should_prompt) {
        const openReport = await promptDailyReportSubmission(prompt.client_name)
        if (openReport) {
          await openSaveSessionNotesModal({
            axios,
            reportDate: prompt.report_date,
            accountOwnerId: prompt.account_owner_id,
          })
        }
      }
      handleClose()
      navigate("/")
      // Full reload so CRM data remounts under the new X-Account-Owner-Id.
      window.location.reload()
    } catch (error) {
      // Keep menu open so the user can retry or pick another account.
      const detail =
        error?.payload ||
        error?.response?.data?.account_owner_id ||
        error?.response?.data?.detail ||
        error?.message
      console.error("Account switch failed", detail || error)
    } finally {
      setSwitchingId(null)
    }
  }

  if (!ownerName) {
    return null
  }

  return (
    <Row sx={{ alignItems: "center", gap: 0.25 }}>
      <BadgeButton
        component="button"
        type="button"
        onClick={handleOpen}
        disabled={!showSwitcher}
        aria-haspopup={showSwitcher ? "menu" : undefined}
        aria-expanded={showSwitcher && Boolean(anchorEl) ? "true" : undefined}
        $clickable={showSwitcher}
      >
        <MemberAccountBadge ownerName={ownerName} variant={badgeVariant} />
        {showSwitcher && (
          <KeyboardArrowDownIcon fontSize="small" sx={{ opacity: 0.8 }} />
        )}
      </BadgeButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuHeader disableRipple disabled>
          <Typography variant="caption" color="text.secondary">
            Switch account
            {isConcierge(profile) || isExecutiveCaller(profile)
              ? " (assigned)"
              : ""}
          </Typography>
        </MenuHeader>
        {loadingList && (
          <MenuItem disabled>
            <CircularProgress size={18} />
          </MenuItem>
        )}
        {!loadingList &&
          accounts.map((account) => {
            const selected = Number(account.id) === Number(activeAccountId)
            const busy = switchingId === Number(account.id)
            return (
              <MenuItem
                key={account.id}
                selected={selected}
                disabled={Boolean(switchingId)}
                onClick={() => handleSelectAccount(account)}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {busy ? (
                    <CircularProgress size={16} />
                  ) : selected ? (
                    <CheckIcon fontSize="small" />
                  ) : null}
                </ListItemIcon>
                <ListItemText
                  primary={account.name || account.email}
                  secondary={account.email}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </MenuItem>
            )
          })}
      </Menu>
    </Row>
  )
}

AccountSwitcher.propTypes = {
  ownerName: PropTypes.string.isRequired,
  badgeVariant: PropTypes.oneOf(["member", "executive_caller", "concierge"]),
}

const BadgeButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$clickable",
})(({ theme, $clickable }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.25),
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  borderRadius: 12,
  cursor: $clickable ? "pointer" : "default",
  color: "inherit",
  font: "inherit",
  "&:disabled": {
    cursor: "default",
  },
}))

const MenuHeader = styled(MenuItem)(({ theme }) => ({
  opacity: 1,
  cursor: "default",
  pointerEvents: "none",
  minHeight: "auto",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
}))
